// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, createContext, useMemo } from 'react';

import { useCounter, useDisclosure } from '@mantine/hooks';

import { useUpdateGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { hasLengthExactly } from 'helpers/helper-types';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { BendCreatorModal } from 'modules/Shop/EvolveFabrication/BendCreator/BendCreatorModal';

type FabricationBendCreatorContextType = {
  /** Opens the bend creator over the current page, for the projects and facilities in the global filters. */
  openBendCreator: () => void;
  /** Whether the global filters have exactly one project selected, as the Fabrication pages require. */
  canOpenBendCreator: boolean;
  /** Increments each time the bend creator creates bends, so pages can refetch their data. */
  bendsCreatedCount: number;
};

const FabricationBendCreatorContext = createContext<FabricationBendCreatorContextType | undefined>(undefined);

/**
 * Hosts the fullscreen {@link BendCreatorModal} for the Fabrication nav, so it can open over any page.
 * Mount it above both the nav and the page outlet.
 */
export const FabricationBendCreatorProvider = ({ children }: { children: ReactNode }) => {
  const {
    globalFilters: { projects, facilities },
  } = useUpdateGlobalFilters();
  const [opened, { open, close }] = useDisclosure(false);
  const [bendsCreatedCount, { increment: onBendsCreated }] = useCounter(0);

  const canOpenBendCreator = hasLengthExactly(projects, 1);
  const value = useMemo(
    () => ({ openBendCreator: open, canOpenBendCreator, bendsCreatedCount }),
    [open, canOpenBendCreator, bendsCreatedCount],
  );

  return (
    <FabricationBendCreatorContext value={value}>
      {children}
      {canOpenBendCreator && (
        <BendCreatorModal
          opened={opened}
          onClose={close}
          onBendsCreated={onBendsCreated}
          selectedProjects={projects}
          selectedFacilities={facilities}
        />
      )}
    </FabricationBendCreatorContext>
  );
};

export const useFabricationBendCreator = () =>
  useGeneralContext(FabricationBendCreatorContext, 'FabricationBendCreator');
